import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  LayoutDashboard,
  PanelsTopLeft,
  Plus,
  SearchIcon,
  ShoppingBag,
} from "@/components/icons";
import {
  Badge,
  Button,
  Input,
  Skeleton,
  ToggleGroup,
  ToggleGroupItem,
  useDebounceCallback,
} from "@/components/kit";
import { cn } from "@/lib/utils";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { CustomJobStatus } from "@/modules/custom-jobs/domain/types";
import { formatLak } from "@/shared/lib/currency";
import { formatDateLocal } from "@/shared/lib/date-time";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useCustomJobBoardQuery, useCustomJobsQuery } from "../api/queries";
import { CustomJobsBoardView } from "../ui/CustomJobsBoardView";
import {
  customJobStatusLabel,
  getCustomJobStatusMeta,
  isCustomJobOverdue,
} from "../ui/job-status";

const STATUS_TABS: Array<{ value: "all" | CustomJobStatus; label: string }> = [
  { value: "all", label: "ທັງໝົດ" },
  { value: "pending_confirm", label: "ລໍຖ້າຢືນຢັນ" },
  { value: "queued", label: "ລໍຖ້າເລີ່ມ" },
  { value: "in_progress", label: "ກຳລັງເຮັດ" },
  { value: "ready", label: "ພ້ອມຮັບ" },
  { value: "completed", label: "ສົ່ງມອບແລ້ວ" },
  { value: "cancelled", label: "ຍົກເລີກ" },
];

const DEFAULT_LIMIT = 20;
type ViewMode = "list" | "board";
type BoardScope = "today" | "all";

export function CustomJobsListPage() {
  const nav = useNavigate();
  const canManage = useActionPermission(["custom_jobs:write"]);
  const [view, setView] = useState<ViewMode>("board");
  const [boardScope, setBoardScope] = useState<BoardScope>("all");
  const [status, setStatus] = useState<"all" | CustomJobStatus>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);

  const debouncedSearch = useDebounceCallback((v: string) => setSearch(v), 400);

  useEffect(() => {
    setOffset(0);
  }, [status, search]);

  const board = useCustomJobBoardQuery({ scope: boardScope }, view === "board");
  const list = useCustomJobsQuery(
    {
      status: status === "all" ? undefined : status,
      search: search || undefined,
      offset,
      limit: DEFAULT_LIMIT,
    },
    view === "list",
  );

  const jobs = list.data?.data ?? [];
  const totalCount = list.data?.meta?.total ?? 0;
  const boardTotal = board.data?.meta?.counts.total ?? 0;

  const subtitle =
    view === "board"
      ? `ຈັດການງານທຳໃຫ້ລູກຄ້າ — ${boardTotal} ງານກຳລັງດຳເນີນ`
      : `ຈັດການງານທຳໃຫ້ລູກຄ້າ — ${totalCount} ລາຍການ`;

  return (
    <>
      <Header />
      <Main className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-2xl text-foreground sm:text-3xl">
              ງານສັ່ງເຮັດ
            </h2>
            <p className="text-muted-foreground text-sm">{subtitle}</p>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canManage ? (
              <Button
                className="rounded-full"
                onClick={() => nav({ to: "/app/custom-jobs/new" })}
              >
                <Plus data-icon="inline-start" />
                ສ້າງງານ
              </Button>
            ) : null}
            {view === "board" ? (
              <ToggleGroup
                type="single"
                value={boardScope}
                onValueChange={(v) => v && setBoardScope(v as BoardScope)}
                variant="outline"
                size="sm"
              >
                <ToggleGroupItem value="today" className="rounded-full px-4">
                  ມື້ນີ້
                </ToggleGroupItem>
                <ToggleGroupItem value="all" className="rounded-full px-4">
                  ທັງໝົດ
                </ToggleGroupItem>
              </ToggleGroup>
            ) : null}
            <ToggleGroup
              type="single"
              value={view}
              onValueChange={(v) => v && setView(v as ViewMode)}
              variant="outline"
              size="sm"
            >
              <ToggleGroupItem value="board" className="rounded-full px-3">
                <LayoutDashboard className="size-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="list" className="rounded-full px-3">
                <PanelsTopLeft className="size-4" />
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>

        {view === "board" ? (
          <CustomJobsBoardView scope={boardScope} board={board} />
        ) : (
          <>
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="rounded-full pl-9"
                placeholder="ຄົ້ນຫາເລກງານ, ຊື່, ເບີ..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  debouncedSearch(e.target.value);
                }}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {STATUS_TABS.map((tab) => (
                <Button
                  key={tab.value}
                  size="sm"
                  variant={status === tab.value ? "default" : "outline"}
                  className="rounded-full"
                  onClick={() => setStatus(tab.value)}
                >
                  {tab.label}
                </Button>
              ))}
            </div>

            {list.isLoading ? (
              <div className="flex flex-col gap-3">
                {["a", "b", "c", "d"].map((id) => (
                  <Skeleton key={id} className="h-24 rounded-xl" />
                ))}
              </div>
            ) : jobs.length === 0 ? (
              <div className="brand-card flex flex-col items-center gap-3 px-6 py-12 text-center">
                <ShoppingBag className="size-10 text-muted-foreground" />
                <p className="text-muted-foreground">ຍັງບໍ່ມີງານສັ່ງເຮັດ</p>
                {canManage ? (
                  <Button
                    className="rounded-full"
                    onClick={() => nav({ to: "/app/custom-jobs/new" })}
                  >
                    <Plus className="size-4" />
                    ສ້າງງານແຮກ
                  </Button>
                ) : null}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {jobs.map((job) => {
                  const meta = getCustomJobStatusMeta(job.status);
                  const overdue = isCustomJobOverdue(job.dueDate, job.status);
                  return (
                    <button
                      key={job.id}
                      type="button"
                      className={cn(
                        "brand-card w-full px-4 py-3 text-left transition-colors hover:border-secondary/45",
                        overdue && "border-destructive/30 bg-destructive/2",
                      )}
                      onClick={() =>
                        nav({
                          to: "/app/custom-jobs/$id",
                          params: { id: job.id },
                        })
                      }
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="font-medium">{job.jobNumber}</p>
                          <p className="text-muted-foreground text-sm">
                            {job.customerName}
                            {job.customerPhone ? ` · ${job.customerPhone}` : ""}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {overdue ? (
                            <Badge
                              variant="destructive"
                              className="rounded-full"
                            >
                              ເກີນກຳນົດ
                            </Badge>
                          ) : null}
                          <Badge
                            variant={meta.variant}
                            className={cn("rounded-full", meta.className)}
                          >
                            {customJobStatusLabel(job.status)}
                          </Badge>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                        <span>{formatLak(job.total)}</span>
                        {job.dueDate ? (
                          <span className="text-muted-foreground">
                            ນັດ {formatDateLocal(job.dueDate)}
                          </span>
                        ) : null}
                        <span className="text-muted-foreground">
                          {job.itemCount} ລາຍການ
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {totalCount > DEFAULT_LIMIT ? (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  className="rounded-full"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - DEFAULT_LIMIT))}
                >
                  ກ່ອນໜ້າ
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full"
                  disabled={offset + DEFAULT_LIMIT >= totalCount}
                  onClick={() => setOffset(offset + DEFAULT_LIMIT)}
                >
                  ຖັດໄປ
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Main>
    </>
  );
}
